/**
* Style injector module
*
* @module Style Injector
*/
define(
    'libs/inject-style/inject-style',[], function () {
        'use strict';

        var
        /**
        * Rule subclass, contains one rule
        *
        * @class Rule
        */
        Rule = (function () {
            function Rule (sel) {
                this._selector = sel;
                this._properties = {};
                this._ref = null;
            }

            /**
            * Contains all of the rule's properties
            *
            * @property _properties
            * @private
            */
            Rule.prototype._properties = {};

            /**
            * The rules selector
            *
            * @property _selector
            * @private
            */
            Rule.prototype._selector = null;

            /**
            * A reference to the generated CSS as present in the
            * injected styles
            *
            * @property _ref
            * @private
            */
            Rule.prototype._ref = null;

            /**
            * Set the rules selector
            *
            * @method setSelector
            * @param {String} sel
            */
            Rule.prototype.setSelector = function (sel) {
                this._selector = sel;
            };

            /**
            * Returns the rules selector
            *
            * @method getSelector
            * @return {String}
            */
            Rule.prototype.getSelector = function () {
                return this._selector;
            };

            /**
            * Add a property to this rule
            *
            * @param {String} property Property name, eg. "background"
            * @param {String|Number} value Property value, eg. "10px" or 10.
            * Note that numbers are automatically converted in to pixel values
            * @return {Rule} this
            */
            Rule.prototype.addProperty = function (property, value) {
                if (typeof value == 'number') {
                    value = value + 'px';
                }

                this._properties[property] = value;

                return this;
            };

            /**
            * Remove a property from this rule
            *
            * @property {String} property Name of the property to remove
            * @return {Rule} this
            */
            Rule.prototype.removeProperty = function (property) {
                delete this._properties[property];

                return this;
            };

            /**
            * Get rule as text (for insertion into style element)
            *
            * @method getAsText
            * @return {String}
            */
            Rule.prototype.getAsText = function () {
                var text = this._selector + ' { ';

                for (var prop in this._properties) {
                    text += prop + ': ' + this._properties[prop] + '; ';
                }

                return text + '}';
            };

            /**
            * Sets the reference for the injected CSS
            *
            * @method setReference
            */
            Rule.prototype.setReference = function () {
                this._ref = this.getAsText();
            };

            /**
            * Get reference for injected CSS
            *
            * @method getReference
            * @return {String}
            */
            Rule.prototype.getReference = function () {
                return this._ref;
            };

            return Rule;
        })(),

        /**
        * Media query subclass, can contain multiple rules
        *
        * @class MediaQuery
        */
        MediaQuery = (function () {
            function MediaQuery (minimum, maximum) {
                if ((minimum === null || minimum === undefined) &&
                    (maximum === null || maximum === undefined)) {
                    throw 'You need to specify either a min- or max-width (or both).';
                }

                if (minimum !== undefined && minimum !== null) {
                    this._min = minimum;
                }

                if (maximum !== undefined && maximum !== null) {
                    this._max = maximum;
                }

                this._rules = {};
                this._ref = '';
            }

            /**
            * An object containing all of the rules to go into the media query
            *
            * @property {Object} _rules
            * @private
            */
            MediaQuery.prototype._rules = {};

            /**
            * A reference to the generated CSS
            *
            * @property {String} _ref
            * @private
            */
            MediaQuery.prototype._ref = '';

            /**
            * The min width for the media query
            *
            * @property {Number} _min
            * @private
            */
            MediaQuery.prototype._min = undefined;

            /**
            * The max width for the media query
            *
            * @property {Number} _max
            * @private
            */
            MediaQuery.prototype._max = undefined;

            /**
            * Set the min width of the media query
            *
            * @method setMinWidth
            * @param {Number} minimum
            */
            MediaQuery.prototype.setMinWidth = function (minimum) {
                this._min = minimum;
            };

            /**
            * Set the max width of the media query
            *
            * @method setMaxWidth
            * @param {Number} minimum
            */
            MediaQuery.prototype.setMaxWidth = function (maximum) {
                this._max = maximum;
            };

            /**
            * Don't use any min width with this media query
            *
            * @method removeMinWidth
            */
            MediaQuery.prototype.removeMinWidth = function () {
                this._min = undefined;
            };

            /**
            * Don't use any max width with this media query
            *
            * @method removeMaxWidth
            */
            MediaQuery.prototype.removeMaxWidth = function () {
                this._max = undefined;
            };

            /**
            * Adds a rule to the media query
            *
            * @method addRule
            * @param {Rule} rule
            */
            MediaQuery.prototype.addRule = function (rule) {
                this._rules[rule.getSelector()] = rule;
            };

            /**
            * Removes the rule from the media query
            *
            * @method removeRule
            * @param {Rule} rule
            */
            MediaQuery.prototype.removeRule = function (rule) {
                delete this._rules[rule.getSelector()];
            };

            /**
            * Get media query as text for injection into the style elem
            *
            * @method getAsText
            * @return {String}
            */
            MediaQuery.prototype.getAsText = function () {
                var text = '@media ',
                    min = this._min,
                    max = this._max;

                if (min !== undefined && max !== undefined) {
                    text += '(min-width: ' + min + 'px) and (max-width: ' + max + 'px) { ';
                } else if (min !== undefined && max === undefined) {
                    text += '(min-width: ' + min + 'px) { ';
                } else if (min === undefined && max !== undefined) {
                    text += '(max-width: ' + max + 'px) { ';
                } else {
                    throw 'You need to specify either a min or max width for the media query';
                }

                for (var ruleKey in this._rules) {
                    text += this._rules[ruleKey].getAsText();
                }

                return text + ' }';
            };

            /**
            * Set the current CSS text as the reference
            *
            * @method setReference
            */
            MediaQuery.prototype.setReference = function () {
                this._ref = this.getAsText();
            };

            /**
            * Get the CSS text reference
            *
            * @method getReference
            * @return {String}
            */
            MediaQuery.prototype.getReference = function () {
                return this._ref;
            };

            return MediaQuery;
        })(),

        /**
        * Style Injector main utility class
        *
        * @class StyleInjector
        */
        StyleInjector = (function () {
            function StyleInjector () {}

            var
            /**
            * The <style> element that we'll use
            *
            * @property styleElement
            * @private
            */
            styleElement,

            /**
            * The full css text that is injected
            *
            * @property cssText
            * @private
            */
            cssText = '';

            /**
            * Retrieve the <style> element (or create it if it doesn't exist)
            *
            * @method _getSheet
            * @private
            * @return {jQuery element}
            */
            function _getSheet () {
                var headElement = document.getElementsByTagName('head')[0];

                if (styleElement === undefined) {
                    styleElement = document.createElement('style');
                    styleElement.setAttribute('type', 'text/css');

                    // Required to work in Webkit browsers
                    _injectText('');

                    headElement.appendChild(styleElement);
                }

                return styleElement;
            }

            /**
            * Injects text to sheet
            *
            * @method _injectText
            * @private
            * @param {string}
            */
            function _injectText (text) {
                cssText += text;

                if (_getSheet().styleSheet) { // IE
                    _getSheet().styleSheet.cssText = cssText;
                }
                else {
                    _getSheet().innerHTML = cssText;
                }
            }

            /**
            * Injects a rule
            *
            * @method _injectRule
            * @private
            * @param {Rule}
            */
            function _injectRule (rule) {
                _injectText(rule.getAsText());

                rule.setReference();
            }

            /**
            * Retracts or removes a rule
            *
            * @method _retractRule
            * @private
            * @param {Rule}
            */
            function _retractRule (rule) {
                var currentStyleContent;

                cssText = cssText.replace(rule.getReference(), '');

                if (_getSheet().styleSheet) { // IE
                    _getSheet().styleSheet.cssText = cssText;
                }
                else {
                    _getSheet().innerHTML = cssText;
                }
            }

            /**
            * Creates a rule without injecting it
            *
            * @method createRule
            * @param {String} selector
            * @param {Object} props
            * @return {Rule}
            */
            StyleInjector.prototype.createRule = function (selector, props) {
                var rule = new Rule(selector);

                for (var prop in props) {
                    rule.addProperty(prop, props[prop]);
                }

                return rule;
            };

            /**
            * Inject a rule
            *
            * @method addRule
            * @param {String} selector
            * @param {Object} props A dictionary containing all of the properties
            * @return {Rule}
            */
            StyleInjector.prototype.addRule = function (selector, props) {
                var rule = this.createRule(selector, props);

                _injectRule(rule);

                return rule;
            };

            /**
            * Remove a rule
            *
            * @method removeRule
            * @param {Rule} rule
            */
            StyleInjector.prototype.removeRule = function (rule) {
                _retractRule(rule);
            };

            /**
            * Reload a rule. Useful if you've already added a rule and
            * then modified some property and want to reapply it.
            *
            * @method reloadRule
            * @param rule
            */
            StyleInjector.prototype.reloadRule = function (rule) {
                _retractRule(rule);
                _injectRule(rule);
            };

            /**
            * Injects a media query into the stylesheet
            *
            * @method addMediaQuery
            * @param {Number} min Min-width for the media query
            * @param {Number} max Max-width for the media query
            * @param {Array} rules A list of rules to add to the media query
            * @return {MediaQuery} Media query object
            */
            StyleInjector.prototype.addMediaQuery = function (min, max, rules) {
                var mq = new MediaQuery(min, max);

                for (var i = 0; i < rules.length; i++) {
                    mq.addRule(rules[i]);
                }

                _injectRule(mq);

                return mq;
            };

            /**
            * Alias for removeRule
            *
            * @method removeMediaQuery
            * @param {MediaQuery} mq
            * @see removeRule
            */
            StyleInjector.prototype.removeMediaQuery = function (mq) {
                this.removeRule(mq);
            };

            /**
            * Alias for reloadRule
            *
            * @method reloadMediaQuery
            * @param {MediaQuery} mq
            * @see reloadRule
            */
            StyleInjector.prototype.reloadMediaQuery = function (mq) {
                this.reloadRule(mq);
            };

            /**
            * Clears all injected stylesheets
            *
            * @method clearInjections
            */
            StyleInjector.prototype.clearInjections = function () {
                cssText = '';

                if (_getSheet().styleSheet) { // IE
                    _getSheet().styleSheet.cssText = cssText;
                }
                else {
                    _getSheet().innerHTML = cssText;
                }
            };

            return StyleInjector;
        })();


        return new StyleInjector();
    }
);

